"use client";

import { useTranslations } from "next-intl";

import { USER_STATUS_COLORS } from "@/shared/config";
import { cn } from "@/shared/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/shared/ui/overlays/tooltip";
import type { PlayerStreamData } from "@/entities/tournament/api/use-tournament-streams";
import { BRAND_COLORS } from "@/shared/config/brand-colors";

export const TWITCH_PURPLE = BRAND_COLORS.twitch;

export function TwitchGlyph({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden>
      <path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714z" />
    </svg>
  );
}

interface TwitchStatusIconProps {
  /** Стрим игрока из батч-эндпоинта. undefined = нет Twitch-канала — иконка не рендерится. */
  stream: PlayerStreamData | undefined;
  className?: string;
}

/**
 * Компактная иконка Twitch с индикатором онлайн/офлайн у игрока (KAN-93).
 * Бэкенд (`TournamentStreamsService`) уже включает в `playerStreams[]` только
 * игроков с привязанным Twitch-каналом — поэтому здесь никакой дополнительной
 * фильтрации по ShowPlayerStreamers/наличию канала не нужно: stream=undefined
 * означает «этого игрока нет в списке», иконки не будет.
 */
export function TwitchStatusIcon({ stream, className }: TwitchStatusIconProps) {
  const t = useTranslations("Tournaments");
  if (!stream) return null;

  // KAN-656: у эфира три состояния, а не два. «Не смогли спросить Twitch» — это не
  // «офлайн»: раньше недоступность Helix красила всех серым, и отличить сломанную
  // интеграцию от пустого эфира было нельзя. Поля может не быть (старый бэкенд) —
  // тогда падаем на прежнее поведение по isLive.
  const status = stream.status ?? (stream.isLive ? "live" : "offline");

  const statusLabel =
    status === "live"
      ? t("stream.live", { channel: stream.twitchLogin })
      : status === "unknown"
        ? t("stream.unknown", { channel: stream.twitchLogin })
        : t("stream.offline", { channel: stream.twitchLogin });

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <a
          href={stream.channelUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          aria-label={statusLabel}
          className={cn(
            "relative inline-flex shrink-0 items-center justify-center hover:opacity-80",
            className,
          )}
          style={{ color: TWITCH_PURPLE }}
        >
          <TwitchGlyph className="h-3.5 w-3.5" />
          <span
            className={cn(
              "absolute -bottom-0.5 -right-0.5 h-1.5 w-1.5 rounded-full ring-1 ring-card",
              status === "live"
                ? USER_STATUS_COLORS.ONLINE
                : status === "unknown"
                  ? // Неизвестно — ни зелёный, ни «офлайн»: пустая точка с обводкой,
                    // чтобы отличалась от обоих определённых состояний.
                    "border border-muted-foreground/70 bg-transparent"
                  : cn(USER_STATUS_COLORS.BUSY, "opacity-60"),
            )}
          />
        </a>
      </TooltipTrigger>
      <TooltipContent>{statusLabel}</TooltipContent>
    </Tooltip>
  );
}
